import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { ChevronDown, Check } from "lucide-react";
import { useTimezone, TZ_PREFERENCES, TZ_OPTIONS } from "../../context/TimezoneProvider";
import { getTimezoneLabel } from "../../utils/utility";
import "./TimezonePicker.scss";

export default function TimezonePicker({ compact = false }) {
  const { preference, effectiveTimezone, timezoneLabel, siteTimezone, browserTimezone, setPreference } = useTimezone();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  // Position the dropdown relative to the trigger button
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 4,
      left: rect.right - 240, // align right edge of dropdown with right edge of trigger
    });
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = e => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Recalculate position on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  const handleToggle = () => {
    if (!open) updatePosition();
    setOpen(prev => !prev);
  };

  const handleSelect = value => {
    setPreference(value);
    setOpen(false);
  };

  const getResolvedIana = optionValue => {
    switch (optionValue) {
      case TZ_PREFERENCES.SITE:
        return siteTimezone || "UTC";
      case TZ_PREFERENCES.LOCAL:
        return browserTimezone;
      case TZ_PREFERENCES.UTC:
        return "UTC";
      default:
        return "UTC";
    }
  };

  const dropdown = open
    ? createPortal(
        <div className="tz-picker__dropdown" ref={dropdownRef} style={{ top: dropdownPos.top, left: dropdownPos.left }}>
          {TZ_OPTIONS.map(opt => {
            const isActive = preference === opt.value;
            const iana = getResolvedIana(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                className={`tz-picker__option ${isActive ? "tz-picker__option--active" : ""}`}
                onClick={() => handleSelect(opt.value)}
              >
                <div className="tz-picker__option-content">
                  <span className="tz-picker__option-label">{opt.label}</span>
                  <span className="tz-picker__option-iana">
                    {iana} ({getTimezoneLabel(iana)})
                  </span>
                </div>
                {isActive && <Check size={14} className="tz-picker__option-check" />}
              </button>
            );
          })}
        </div>,
        document.body
      )
    : null;

  return (
    <div className={`tz-picker ${compact ? "tz-picker--compact" : ""}`}>
      <button
        type="button"
        className="tz-picker__trigger"
        ref={triggerRef}
        onClick={handleToggle}
        title={effectiveTimezone}
      >
        <span className="tz-picker__value">{timezoneLabel}</span>
        <ChevronDown size={12} className={`tz-picker__chevron ${open ? "tz-picker__chevron--open" : ""}`} />
      </button>
      {dropdown}
    </div>
  );
}

TimezonePicker.propTypes = {
  compact: PropTypes.bool,
};
